import MolGrid, { Column } from "@moltech/molui/molgrid";
import React, { useEffect, useRef, useState } from "react";
import RadioButton from "../../../../../../components/ui/inputs/radioButton/RadioButton";
import Buttons from "../../../../../../components/ui/button/Buttons";
import CardSection from "../../../../../../components/ui/card/CardSection";
import {
  useAddContractDepartmentCommentMutation,
  useLazyGetContractDepartmentCommentQuery,
  useLazyGetContractUpcomingInvoicesNewQuery,
} from "../../../../../../app/services/contractAPI";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import Iconify from "../../../../../../components/ui/iconify/Iconify";
import CustomCenterModel from "../../../../../../components/ui/customCenterModel/CustomCenterModel";
import { AppIcons } from "../../../../../../data/appIcons";
import FormTextAreaFields from "../../../../../../components/finalForms/formFields/FormTextAreaField";
import { useNavigate } from "react-router-dom";
import Checkbox from "../../../../../../components/finalForms/ui/inputs/checkBox/CheckBox";
import ToastService from "../../../../../../services/toastService/ToastService";

const instrumentTypeOptions = [
  { label: "Overdue/Current", value: "Overdue/Current" },
  { label: "Upcoming", value: "Upcoming" },
  { label: "Future", value: "Future" },
  { label: "All Others", value: "All Others" },
];

const CreateInvoices = () => {
  const gridRef = useRef();

  const [selectedRadioOption, setSelectedRadioOption] = useState();
  const [isSelectChecked, setIsSelectChecked] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [selectedCheckboxRows, setSelectedCheckboxRows] = useState([]);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");
  const [commentData, setCommentData] = useState("");
  const navigate = useNavigate();

  /*api call*/
  const [
    getContractUpcomingInvoicesNew,
    {
      isFetching: isContractUpcomingInvoicesFetching,
      isSuccess: isContractUpcomingInvoicesFetched,
      data: ContractUpcomingInvoicesData,
    },
  ] = useLazyGetContractUpcomingInvoicesNewQuery();

  const [
    getContractDepartmentComment,
    {
      isFetching: isContractDepartmentCommentFetching,
      isSuccess: isContractDepartmentCommentFetched,
      data: ContractDepartmentCommentData,
    },
  ] = useLazyGetContractDepartmentCommentQuery();

  const [
    addContractDepartmentComment,
    {
      isSuccess: isContractDepartmentCommentSuccess,
      data: addContractDepartmentCommentData,
    },
  ] = useAddContractDepartmentCommentMutation();

  useEffect(() => {
    let req = { plContractKey: 0, plDepartmentKey: 0, pbShowInactive: false };
    getContractUpcomingInvoicesNew(req);
  }, [getContractUpcomingInvoicesNew]);

  useEffect(() => {
    if (!originalData?.length) {
      setGridData([]);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const threeMonthsLater = new Date(today);
    threeMonthsLater.setMonth(today.getMonth() + 3);
    setSelectedCheckboxRows([]);
    const filtered = originalData.filter((item) => {
      const nextBillDate = item.nextBillDate
        ? new Date(item.nextBillDate)
        : null;

      if (!isSelectChecked && item.bExpired) {
        return false;
      }

      switch (selectedRadioOption || "Overdue/Current") {
        case "Overdue/Current":
          return (
            item.bSkipExceptAll === false &&
            nextBillDate &&
            nextBillDate <= today
          );

        case "Upcoming":
          return (
            item.bSkipExceptAll === false &&
            nextBillDate &&
            nextBillDate > today &&
            nextBillDate <= threeMonthsLater
          );

        case "Future":
          return (
            item.bSkipExceptAll === false &&
            nextBillDate &&
            nextBillDate > threeMonthsLater
          );

        case "All Others":
          return item.bSkipExceptAll === true;

        default:
          return true;
      }
    }).map((item) => ({...item}));
     setGridData(filtered);
  }, [originalData, selectedRadioOption, isSelectChecked]);

  useEffect(() => {
    if (
      !isContractUpcomingInvoicesFetching &&
      isContractUpcomingInvoicesFetched &&
      ContractUpcomingInvoicesData
    ) {
      if (ContractUpcomingInvoicesData) {
        setOriginalData(ContractUpcomingInvoicesData);
      }
    }
  }, [
    isContractUpcomingInvoicesFetching,
    isContractUpcomingInvoicesFetched,
    ContractUpcomingInvoicesData,
  ]);

  useEffect(() => {
    if (
      !isContractDepartmentCommentFetching &&
      isContractDepartmentCommentFetched &&
      ContractDepartmentCommentData
    ) {
      if (ContractDepartmentCommentData) {
        setCommentData(ContractDepartmentCommentData);
      }
    }
  }, [
    isContractDepartmentCommentFetching,
    isContractDepartmentCommentFetched,
    ContractDepartmentCommentData,
  ]);

  useEffect(() => {
    if (
      isContractDepartmentCommentSuccess &&
      addContractDepartmentCommentData
    ) {
      if (addContractDepartmentCommentData) {
        ToastService.success(addContractDepartmentCommentData?.errorMessage);
        handleCloseModel();
      }
    }
  }, [isContractDepartmentCommentSuccess, addContractDepartmentCommentData]);

  const textEditor = (options) => {
    return (
      <InputText
        value={options.value ?? ""}
        onChange={(e) => options?.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
        onFocus={(e) => {
          e.target.select();
        }}
      />
    );
  };

  const calenderEditor = (options) => {
    return (
      <Calendar
        selectionMode="single"
        value={options.value ? new Date(options.value) : null}
        onChange={(e) => options.editorCallback(e.value)}
      />
    );
  };

  const onCellEditComplete = (e) => {
    const { rowData, field, newValue } = e;
    setGridData((prevData) =>
      prevData.map((row) => {
        if (row.lDepartmentKey !== rowData.lDepartmentKey) {
          return row;
        }
        let finalValue = newValue;
        if (field === "amountToBill") {
          const num = parseFloat(newValue) || 0;
          finalValue = `$${num.toFixed(2)}`;
        }
        return { ...row, [field]: finalValue };
      })
    );
  };

  const handleRadioChange = (e) => {
    setSelectedRadioOption(e.target.value);
  };

  const handleCheckboxChange = () => {
    setIsSelectChecked(!isSelectChecked);
  };

  const OnSelectCommentsData = (row) => {
    setIsModelOpen(true);
    setSelectedRow(row);
    getContractDepartmentComment({
      plContractKey: row.lContractKey,
      plDepartmentKey: row.lDepartmentKey,
    });
  };

  const handleCloseModel = () => {
    setIsModelOpen(!isModelOpen);
    setCommentData(null);
    setSelectedRow(null);
  };

  const handleChangeData = (dataField, value) => {
    if (dataField === "Comments") {
      setCommentData((prevData) => ({
        ...prevData,
        sNextInvoiceComment: value,
      }));
    }
    if (dataField === "bSaveInvoiceComment") {
      setCommentData((prevData) => ({
        ...prevData,
        bSaveInvoiceComment: value,
      }));
    }
  };

  const handaleSave = () => {
    const req = {
      plContractKey: selectedRow?.lContractKey,
      plDepartmentKey: selectedRow?.lDepartmentKey,
      psComment: commentData.sNextInvoiceComment,
      pbSaveInvoiceComment: commentData.bSaveInvoiceComment || false,
    };
    addContractDepartmentComment(req);
  };

  const getCtxMenuItems = (row) => {
    if (!row) {
      return [];
    }
    return [
      {
        label: "Open Contract",
        command: () => {
          navigate(`/EditContracts?contract=${row?.lContractKey}`);
        },
      },
    ];
  };

  return (
    <>
      <CardSection>
        <div className="create-invoice-detail">
          <div className="radio-selection-section">
            <div className="radio-box-part">
              <RadioButton
                onChange={handleRadioChange}
                selectedOption={selectedRadioOption || "Overdue/Current"}
                options={instrumentTypeOptions}
              />
            </div>
            <div className="check-box-sec ml-5">
              <Checkbox
                key="expiredContracts"
                name="expiredContracts"
                label="Show Expired Contracts"
                checked={isSelectChecked}
                onChange={handleCheckboxChange}
              />
            </div>
            <div className="right-side-button-checkbox">
              <div className="btn-sec">
                <Buttons
                  buttonText={"Preview Invoices"}
                  buttonTypeClassName="btn blue-btn"
                  // onClick={() => setIsModelOpen(true)}
                />
                <Buttons
                  buttonText={"Create Invoices"}
                  buttonTypeClassName="btn blue-btn"
                  // onClick={() => setIsModelOpen(true)}
                />
              </div>
            </div>
          </div>
          <div className="scrollable-grid">
            <MolGrid
              ref={gridRef}
              editMode="cell"
              value={gridData}
              grouping
              expandableRowGroups={true}
              paginator={false}
              scrollable
              scrollHeight="flex"
              filterDisplay="row"
              sortMode="single"
              selectionMode="checkbox"
              showGridlines
              resizableColumns
              selection={selectedCheckboxRows}
              ctxMenuItems={getCtxMenuItems}
              onSelectionChange={(e) => setSelectedCheckboxRows(e.value)}
            >
              <Column
                field="sContractName1"
                header="Contract"
                bodyStyle={{ textAlign: "left" }}
                style={{ width: "22%" }}
                grouping
                sortable
              />
              <Column
                field="sClientName1"
                header="Client"
                bodyStyle={{ textAlign: "left" }}
                style={{ width: "22%" }}
                editor={textEditor}
                onCellEditComplete={onCellEditComplete}
                grouping
                sortable
              />
              <Column
                field="sDepartmentName"
                header="Department"
                bodyStyle={{ textAlign: "center" }}
                style={{ width: "15%" }}
                editor={textEditor}
                onCellEditComplete={onCellEditComplete}
                grouping
                sortable
              />
              <Column
                field="create"
                header="Create"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                selectionMode="multiple"
                className="center-align-column"
              />
              <Column
                field="sInstallmentType"
                header="Installment Type"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                grouping
                sortable
              />
              <Column
                field="lastBillDate"
                header="Previous Invoice Date"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                colType="datetime"
                format="mm/dd/yyyy"
                grouping
                sortable
              />
              <Column
                field="nextBillDate"
                header="Invoice Date"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                colType="datetime"
                format="mm/dd/yyyy"
                editor={calenderEditor}
                onCellEditComplete={onCellEditComplete}
                body={(row) =>
                  row.nextBillDate
                    ? new Date(row.nextBillDate).toLocaleDateString("en-US")
                    : ""
                }
                grouping
                sortable
              />
              <Column
                field="expirationDate"
                header="Expiration Date"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                editor={calenderEditor}
                onCellEditComplete={onCellEditComplete}
                colType="datetime"
                format="mm/dd/yyyy"
                body={(row) =>
                  row.expirationDate
                    ? new Date(row.expirationDate).toLocaleDateString("en-US")
                    : ""
                }
                grouping
                sortable
              />
              <Column
                field="coveragePeriod"
                header="Coverage Period"
                bodyStyle={{ textAlign: "Center" }}
                style={{ width: "5%" }}
                editor={textEditor}
                onCellEditComplete={onCellEditComplete}
                grouping
                sortable
              />
              <Column
                field="amountToBill"
                header="Invoice Amount"
                bodyStyle={{ textAlign: "Right" }}
                style={{ width: "5%" }}
                editor={textEditor}
                onCellEditComplete={onCellEditComplete}
                colType="money"
                grouping
                sortable
              />
              <Column
                field="sNextInvoiceComment"
                header="Comment"
                bodyStyle={{ textAlign: "Center" }}
                body={(row) => (
                  <span onClick={() => OnSelectCommentsData(row)}>
                    {row?.sNextInvoiceComment?.length > 0 ? (
                      <Iconify icon="raphael:view" />
                    ) : (
                      <Iconify icon="fluent:add-12-filled" />
                    )}
                  </span>
                )}
                style={{ width: "6%" }}
                grouping
                sortable
                className="action-btn-column"
              />
            </MolGrid>
          </div>
        </div>
      </CardSection>
      <div className="row">
        <CustomCenterModel
          isOpen={isModelOpen}
          useCustomBg={true}
          modalTitle={"Invoice Comment"}
          contentClass="content-35"
          onClose={handleCloseModel}
          modalTitleIcon={AppIcons.AddIcon}
        >
          <div>
            <label className="font-weight-bold ml-1 mb-2">
              {selectedRow?.sContractName1 || ""}
            </label>
          </div>
          <FormTextAreaFields
            name={"Invoice Comment"}
            dataField={"Comments"}
            value={commentData?.sNextInvoiceComment || ""}
            onChange={handleChangeData}
          />

          <Checkbox
            name="bSaveInvoiceComment"
            checked={commentData?.bSaveInvoiceComment}
            dataField="bSaveInvoiceComment"
            onChange={handleChangeData}
            isdisable={false}
            label="Carry Forword"
          />

          <div className="d-flex justify-content-center button-submit ">
            <Buttons
              buttonText="Save"
              buttonTypeClassName="btn blue-btn w-full"
              onClick={handaleSave}
              isDisable={
                commentData?.sNextInvoiceComment?.length > 0 ? false : true
              }
            />
            <Buttons
              buttonText="Cancel"
              buttonTypeClassName="btn secondary-btn ml-4"
              onClick={handleCloseModel}
            />
          </div>
        </CustomCenterModel>
      </div>
    </>
  );
};

export default CreateInvoices;
